/**
 * Payment Service - Handles all payment-related operations with Tap Payments
 * Manages payment intents, wallet balance, transactions, and teacher payouts
 */

import { supabase } from './supabaseClient';
import { API_ENDPOINTS } from './config/apiConfig';

/**
 * Create payment intent for a booking
 * @param {string} bookingId - Booking ID
 * @param {number} sessionPrice - Original session price
 * @param {boolean} useWalletBalance - Whether to use wallet balance
 * @param {number} walletAmountToUse - Amount of wallet balance to use
 * @returns {Promise<Object>} Payment intent response with paymentUrl or fullyPaidByWallet flag
 */
export async function createPaymentIntent(bookingId, sessionPrice, useWalletBalance = false, walletAmountToUse = 0) {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;

    if (!token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(API_ENDPOINTS.PAYMENT_CREATE_INTENT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bookingId,
        sessionPrice,
        useWalletBalance,
        walletAmountToUse
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create payment intent');
    }

    const data = await response.json();
    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get payment status by charge ID
 * @param {string} chargeId - Tap charge ID
 * @returns {Promise<Object>} Payment status
 */
export async function getPaymentStatus(chargeId) {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;

    const response = await fetch(`${API_ENDPOINTS.PAYMENT_STATUS}/${chargeId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payment status');
    }

    const data = await response.json();
    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error getting payment status:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get booking details by ID
 * @param {string} bookingId - Booking ID
 * @returns {Promise<Object>} Booking details
 */
export async function getBookingDetails(bookingId) {
  try {
    const { data, error } = await supabase
      .from('booking_requests')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (error) throw error;

    return {
      success: true,
      booking: data
    };
  } catch (error) {
    console.error('Error getting booking details:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get student wallet balance
 * @param {string} studentId - Student user ID (optional, defaults to current user)
 * @returns {Promise<Object>} Wallet balance information
 */
export async function getWalletBalance(studentId = null) {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;

    if (!token) {
      throw new Error('User not authenticated');
    }

    let url = API_ENDPOINTS.WALLET_BALANCE;
    if (studentId) {
      url += `?studentId=${studentId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get wallet balance');
    }

    const data = await response.json();
    return {
      success: true,
      balance: data.balance || 0,
      currency: data.currency || 'BHD'
    };
  } catch (error) {
    console.error('Error getting wallet balance:', error);
    return {
      success: false,
      error: error.message,
      balance: 0
    };
  }
}

/**
 * Get wallet transaction history
 * @param {number} limit - Number of transactions to retrieve (default: 50)
 * @returns {Promise<Object>} Transaction history
 */
export async function getWalletTransactions(limit = 50) {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;

    if (!token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`${API_ENDPOINTS.WALLET_TRANSACTIONS}?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get transaction history');
    }

    const data = await response.json();
    return {
      success: true,
      transactions: data.transactions || []
    };
  } catch (error) {
    console.error('Error getting wallet transactions:', error);
    return {
      success: false,
      error: error.message,
      transactions: []
    };
  }
}

/**
 * Request teacher payout for completed sessions
 * @param {Array<string>} bookingIds - Array of completed booking IDs to cash out
 * @param {string} payoutMethod - Payout method (e.g., 'bank_transfer', 'benefit_pay')
 * @param {Object} bankDetails - Bank account details
 * @returns {Promise<Object>} Payout request result
 */
export async function requestTeacherPayout(bookingIds, payoutMethod, bankDetails) {
  try {
    const token = (await supabase.auth.getSession()).data.session?.access_token;

    if (!token) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(API_ENDPOINTS.TEACHER_PAYOUT_REQUEST, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bookingIds,
        payoutMethod,
        bankDetails
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to request payout');
    }

    const data = await response.json();
    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error requesting payout:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get teacher's bookings eligible for payout
 * Sessions are eligible if: accepted, paid by student, not yet in a payout
 * Matches admin panel query logic exactly for consistency
 * @param {string} teacherEmail - Teacher email address
 * @returns {Promise<Object>} Eligible bookings for payout
 */
export async function getEligiblePayoutBookings(teacherEmail = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const targetEmail = teacherEmail || user?.email;

    if (!targetEmail) {
      throw new Error('Teacher email not found');
    }

    console.log('[paymentService] Fetching eligible bookings for:', targetEmail);

    // Query matching admin panel exactly: payment_status='paid', status='accepted', payout_id=null
    // NO date filter - includes future sessions that are already paid (matches admin)
    const { data, error } = await supabase
      .from('booking_requests')
      .select('*')
      .eq('teacher_email', targetEmail)
      .eq('status', 'accepted')
      .eq('payment_status', 'paid')
      .is('payout_id', null)
      .order('date', { ascending: false });

    if (error) {
      console.error('[paymentService] Eligible bookings query error:', error);
      throw error;
    }

    console.log('[paymentService] Eligible bookings found:', data?.length || 0);
    if (data && data.length > 0) {
      console.log('[paymentService] Sample booking:', {
        id: data[0].id,
        date: data[0].date,
        payment_status: data[0].payment_status,
        total_price: data[0].total_price,
        teacher_payout_amount: data[0].teacher_payout_amount
      });
    }

    return {
      success: true,
      bookings: data || []
    };
  } catch (error) {
    console.error('Error getting eligible payout bookings:', error);
    return {
      success: false,
      error: error.message,
      bookings: []
    };
  }
}

/**
 * Get teacher payout history
 * @param {string} teacherEmail - Teacher email address (optional, defaults to current user)
 * @returns {Promise<Object>} Payout history
 */
export async function getPayoutHistory(teacherEmail = null) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const targetEmail = teacherEmail || user?.email;

    if (!targetEmail) {
      throw new Error('Teacher email not found');
    }

    console.log('[paymentService] Fetching payout history for:', targetEmail);

    const { data, error } = await supabase
      .from('teacher_payouts')
      .select('*')
      .eq('teacher_email', targetEmail)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[paymentService] Payout history query error:', error);
      // If table doesn't exist, return empty array instead of throwing
      if (error.code === '42P01' || error.message?.includes('does not exist')) {
        console.warn('[paymentService] teacher_payouts table may not exist yet');
        return { success: true, payouts: [] };
      }
      throw error;
    }

    console.log('[paymentService] Payouts found:', data?.length || 0);
    return {
      success: true,
      payouts: data || []
    };
  } catch (error) {
    console.error('Error getting payout history:', error);
    return {
      success: false,
      error: error.message,
      payouts: []
    };
  }
}

/**
 * Calculate payment breakdown (session price + platform fee)
 * Uses same calculation as backend: 0.500 BD + 15%
 * @param {number} sessionPrice - Original session price
 * @returns {Object} Pricing breakdown
 */
export function calculatePricing(sessionPrice) {
  const PLATFORM_BASE_COMMISSION = 0.500;
  const PLATFORM_COMMISSION_PERCENTAGE = 0.15;

  const baseCommission = PLATFORM_BASE_COMMISSION;
  const percentageCommission = sessionPrice * PLATFORM_COMMISSION_PERCENTAGE;
  const totalCommission = baseCommission + percentageCommission;
  const teacherPayout = sessionPrice;
  const studentTotal = sessionPrice + totalCommission;

  return {
    sessionPrice: parseFloat(sessionPrice.toFixed(3)),
    platformCommission: parseFloat(totalCommission.toFixed(3)),
    baseCommission: parseFloat(baseCommission.toFixed(3)),
    percentageCommission: parseFloat(percentageCommission.toFixed(3)),
    teacherPayout: parseFloat(teacherPayout.toFixed(3)),
    studentTotal: parseFloat(studentTotal.toFixed(3))
  };
}

/**
 * Format currency amount for display (BHD with 3 decimal places)
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'BD')
 * @returns {string} Formatted amount
 */
export function formatCurrency(amount, currency = 'BD') {
  return `${parseFloat(amount).toFixed(3)} ${currency}`;
}

export default {
  createPaymentIntent,
  getPaymentStatus,
  getBookingDetails,
  getWalletBalance,
  getWalletTransactions,
  requestTeacherPayout,
  getEligiblePayoutBookings,
  getPayoutHistory,
  calculatePricing,
  formatCurrency
};
